import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import KpiCardLoadingShimmer from "../../../components/common/reportDashBShimmer/KpiCardLoadingShimmer";
import { ErrorRetry } from "../../../components/common/ErrorRetry";
import { KPICard } from "../../../components/reportDashboard/reportSummary/KPICard";
import { SearchBar } from "../users/UsersScreenSections";
import { getErrorMessage } from "../../../utils/error";
import {
  getStatusColor,
  HEADER_CELL_STYLES,
  PAPER_STYLES,
} from "./trialsScreen.constants";

export function TrialsHeader() {
  return (
    <Box className="mb-6">
      <Typography variant="h5" className="font-bold text-slate-900">
        Trial Management
      </Typography>
      <Typography variant="body2" className="text-slate-600 mt-1">
        Review subscription KPIs and monitor user trial activity
      </Typography>
    </Box>
  );
}

export function TrialsSummaryCards({
  loading,
  isError,
  error,
  onRetry,
  summaryCards,
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: "repeat(4, 1fr)",
        },
        gap: 3,
        mb: 4,
      }}
    >
      {loading ? (
        Array.from({ length: 4 }).map((_, idx) => (
          <KpiCardLoadingShimmer key={idx} />
        ))
      ) : isError ? (
        <Box sx={{ gridColumn: "1 / -1" }}>
          <ErrorRetry
            message={getErrorMessage(error)}
            onRetry={onRetry}
          />
        </Box>
      ) : summaryCards.length > 0 ? (
        summaryCards.map((kpi, index) => (
          <KPICard
            key={kpi.label || index}
            label={kpi.label}
            value={kpi.value}
            status={kpi.status}
            changePercentage={kpi.changePercentage}
            history={kpi.history}
            secondaryValue={kpi.secondaryValue}
            currency={kpi.currency}
          />
        ))
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            gridColumn: "1 / -1",
          }}
        >
          <Typography>No subscription summary available.</Typography>
        </Paper>
      )}
    </Box>
  );
}

export function TrialsUsersTable({
  searchQuery,
  onSearchChange,
  usersLoading,
  usersError,
  usersErrorObj,
  onRetryUsers,
  trialRows,
  onViewUser,
  page,
  limit,
  totalCount,
  onPageChange,
  onLimitChange,
}) {
  return (
    <>
      <SearchBar value={searchQuery} onChange={onSearchChange} />

      {usersError ? (
        <ErrorRetry
          message={getErrorMessage(usersErrorObj)}
          onRetry={onRetryUsers}
        />
      ) : (
        <Paper elevation={0} sx={PAPER_STYLES}>
          <Box sx={{ p: 3, borderBottom: "1px solid #E2E8F0" }}>
            <Typography variant="h6" className="font-semibold">
              Users
            </Typography>
          </Box>

          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                <TableRow>
                  <TableCell sx={HEADER_CELL_STYLES}>User</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>Plan</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>Start Date</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>End Date</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>Progress</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>Days Left</TableCell>
                  <TableCell sx={HEADER_CELL_STYLES}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography className="text-slate-600 py-6 text-center">
                        Loading users...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : trialRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography className="text-slate-600 py-6 text-center">
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  trialRows.map((row) => {
                    const statusColor = getStatusColor(row.status);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => onViewUser(row.accountId)}
                        sx={{
                          cursor: row.accountId ? "pointer" : "default",
                          "&:hover": {
                            bgcolor: "#F8FAFC",
                          },
                        }}
                      >
                        <TableCell>
                          <Box className="flex items-center gap-3">
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "#1F4FFF",
                              }}
                            >
                              {row.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography className="font-semibold text-slate-900">
                                {row.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                className="text-slate-500"
                              >
                                {row.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.plan}
                            size="small"
                            sx={{
                              bgcolor: "#1F4FFF",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography className="text-slate-700">
                            {row.startDate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography className="text-slate-700">
                            {row.endDate}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.progress === null ? (
                            <Typography className="text-slate-500">
                              -
                            </Typography>
                          ) : (
                            <Box sx={{ width: 120 }}>
                              <Box className="flex items-center gap-2 mb-1">
                                <Typography
                                  variant="body2"
                                  className="text-slate-600 text-xs"
                                >
                                  {row.progress}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={row.progress}
                                sx={{
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: "#F1F5F9",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor:
                                      row.progress >= 90
                                        ? "#DC2626"
                                        : "#1F4FFF",
                                    borderRadius: 3,
                                  },
                                }}
                              />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography
                            className={
                              row.daysRemaining !== null &&
                              row.daysRemaining <= 1
                                ? "text-red-600 font-bold"
                                : "text-slate-700 font-semibold"
                            }
                          >
                            {row.daysRemaining === null
                              ? "-"
                              : `${row.daysRemaining} days`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            sx={{
                              bgcolor: statusColor.bg,
                              color: statusColor.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_event, newPage) => onPageChange(newPage)}
            rowsPerPage={limit}
            onRowsPerPageChange={(event) => {
              onLimitChange(parseInt(event.target.value, 10));
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      )}
    </>
  );
}
